import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { MIN_QUERY, type SearchGroup } from './searchModel';

const API_URL = import.meta.env.VITE_API_URL || '/api';

export function useDebounced<T>(value: T, ms = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

/** POST, not GET: the query stays out of access logs (backend core/search/routes.ts). */
export async function fetchSearch(q: string, options: { limit?: number; providers?: string[] } = {}): Promise<SearchGroup[]> {
  const res = await axios.post<{ groups: SearchGroup[] }>(`${API_URL}/search`, { q, ...options }, { withCredentials: true });
  return res.data.groups;
}

export function useSearch(query: string, options: { limit?: number; providers?: string[] } = {}) {
  const q = useDebounced(query.trim());
  const enabled = q.length >= MIN_QUERY;
  const result = useQuery({
    queryKey: ['search', q, options.limit ?? 5, options.providers ?? null],
    queryFn: () => fetchSearch(q, options),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  return { ...result, groups: enabled ? result.data ?? [] : [], debouncedQuery: q };
}
